/**
 * Production error monitoring client.
 *
 * Batches client-side errors and ships them to the `report-error` edge
 * function, which stores them and emails an alert on new signatures,
 * spikes, and fatals.
 *
 * Design notes:
 *  - Only active in production builds (opt-in override for local testing).
 *  - Dedupes identical signatures within a short window so one broken render
 *    loop cannot flood the pipeline.
 *  - Caps events per session so a pathological page stays cheap.
 *  - Flushes on a timer, when the batch fills, and on page hide (sendBeacon).
 */

import { supabase } from "@/integrations/supabase/client";

export interface ReportableError {
  message: string;
  stack?: string | null;
  severity?: "info" | "warning" | "error" | "fatal";
  source?: string;
  route?: string;
  url?: string;
  release?: string;
  tags?: Record<string, unknown>;
  extra?: Record<string, unknown>;
  breadcrumbs?: unknown[];
}

const FLUSH_INTERVAL_MS = 5_000;
const MAX_BATCH = 10;
const DEDUPE_WINDOW_MS = 30_000;
const MAX_EVENTS_PER_SESSION = 40;

/** Noise we never want to page on: extension chatter, aborted fetches, etc. */
const IGNORED_PATTERNS: RegExp[] = [
  /ResizeObserver loop/i,
  /Non-Error promise rejection captured/i,
  /^Script error\.?$/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /Failed to fetch dynamically imported module/i, // handled by lazyRetry
  /AbortError/i,
  /Load failed$/i,
];

const ENDPOINT = `${import.meta.env.VITE_SUPABASE_URL ?? ""}/functions/v1/report-error`;
const ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ?? "";

let queue: ReportableError[] = [];
let timer: ReturnType<typeof setTimeout> | null = null;
let initialized = false;
let sessionCount = 0;
let forceEnabled = false;
const lastSeen = new Map<string, number>();

export function getRelease(): string {
  const env = import.meta.env as Record<string, string | undefined>;
  return env.VITE_BUILD_ID || env.VITE_COMMIT_SHA || (import.meta.env.PROD ? "prod" : "dev");
}

function isEnabled() {
  if (forceEnabled) return true;
  if (typeof window === "undefined") return false;
  return import.meta.env.PROD === true;
}

/** Enable reporting outside production (used by smoke tests / QA). */
export function enableErrorReporting() {
  forceEnabled = true;
}

function signature(e: ReportableError) {
  const frame = (e.stack ?? "").split("\n").slice(0, 2).join("|");
  return `${e.message}|${frame}|${e.route ?? ""}`;
}

function shouldIgnore(message: string) {
  return IGNORED_PATTERNS.some((re) => re.test(message));
}

function scheduleFlush() {
  if (timer) return;
  timer = setTimeout(() => {
    timer = null;
    void flushErrors();
  }, FLUSH_INTERVAL_MS);
}

/** Queue an error for delivery. Safe to call anywhere; never throws. */
export function reportError(event: ReportableError) {
  try {
    if (!isEnabled()) return;
    if (!event?.message) return;
    if (shouldIgnore(event.message)) return;
    if (sessionCount >= MAX_EVENTS_PER_SESSION) return;

    const sig = signature(event);
    const now = Date.now();
    const seenAt = lastSeen.get(sig);
    if (seenAt && now - seenAt < DEDUPE_WINDOW_MS) return;
    lastSeen.set(sig, now);

    sessionCount += 1;
    queue.push({
      severity: "error",
      source: "manual",
      route: window.location.pathname,
      url: window.location.href,
      release: getRelease(),
      ...event,
    });

    if (queue.length >= MAX_BATCH) {
      void flushErrors();
    } else {
      scheduleFlush();
    }
  } catch {
    /* monitoring must never break the app */
  }
}

/** Send everything currently queued. Resolves to the number of events sent. */
export async function flushErrors(useBeacon = false): Promise<number> {
  if (queue.length === 0) return 0;
  const events = queue;
  queue = [];
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }

  const body = JSON.stringify({ events });

  try {
    if (useBeacon && typeof navigator !== "undefined" && "sendBeacon" in navigator) {
      // Beacon cannot set an Authorization header; the endpoint accepts
      // anonymous reports and simply leaves user_id null.
      navigator.sendBeacon(
        `${ENDPOINT}?apikey=${encodeURIComponent(ANON_KEY)}`,
        new Blob([body], { type: "application/json" }),
      );
      return events.length;
    }

    let token: string | undefined;
    try {
      const { data } = await supabase.auth.getSession();
      token = data.session?.access_token;
    } catch {
      /* anonymous report */
    }

    await fetch(ENDPOINT, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: ANON_KEY,
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body,
      keepalive: true,
    });
    return events.length;
  } catch {
    // Drop on failure rather than retrying forever; keeps the client quiet.
    return 0;
  }
}

/**
 * Install global handlers for uncaught errors and unhandled promise
 * rejections, plus a page-hide flush. Idempotent.
 */
export function initErrorMonitoring() {
  if (initialized || typeof window === "undefined") return;
  initialized = true;

  window.addEventListener("error", (event) => {
    const err = event.error as Error | undefined;
    reportError({
      message: err?.message || event.message || "Unknown window error",
      stack: err?.stack ?? null,
      source: "window.onerror",
      severity: "error",
      extra: { filename: event.filename, lineno: event.lineno, colno: event.colno },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const err = reason instanceof Error ? reason : null;
    reportError({
      message: err?.message || String(reason ?? "Unhandled promise rejection"),
      stack: err?.stack ?? null,
      source: "unhandledrejection",
      severity: "error",
    });
  });

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") void flushErrors(true);
  });

  window.addEventListener("pagehide", () => {
    void flushErrors(true);
  });
}

/** Test helper: clears queue, dedupe cache, and session counters. */
export function __resetErrorReportingForTests() {
  queue = [];
  lastSeen.clear();
  sessionCount = 0;
  forceEnabled = false;
  initialized = false;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
